import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  from,
  type ServerError,
  type StoreObject,
  type TypePolicies,
} from '@apollo/client';
import type { KeyFieldsFunction } from '@apollo/client/cache/inmemory/policies';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { browserLogger, isAbortError } from '@newfront-insurance/dd-rum';
import type {
  AnyAuthProviderContext,
  AuthProviderContext,
  AuthProviderProps,
  AuthSwapProviderContext,
  AuthSwapProviderOptions,
} from '@newfront-insurance/next-auth';
import { useNotifications } from '@newfront-insurance/next-notifications';
import type { Provider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';
import type { ConfigType } from '@newfront-insurance/shared-public-config';
import { readFragment } from 'gql.tada';
import { useState, type PropsWithChildren } from 'react';

import { createPersistedQueryLink } from './apq';
import { graphql as gqlTada } from './graphql';
import { createLoggerLink } from './logging/logger-link';
import { createNotificationLink } from './notifications/notification-link';

// Import module augmentation to extend Apollo Client types with notification context
import './notifications/apollo-client-extensions';

interface NewfrontApolloProviderProps extends PropsWithChildren {
  authProvider:
    | Provider<AuthSwapProviderContext, AuthSwapProviderOptions>
    | Provider<AuthProviderContext, AuthProviderProps>;
  config: ConfigType;
  /**
   * Enable Automatic Persisted Queries (APQ) for improved performance and caching.
   * When enabled, queries are sent with a hash instead of the full query text,
   * and GET requests are used for cacheable queries.
   * @default true
   */
  enableAPQ?: boolean;
  /**
   * Additional cache type policies merged into the base policies. Use this to
   * add app-specific normalization/merge behaviour without changing the shared
   * defaults for other apps.
   */
  additionalTypePolicies?: TypePolicies;
}

const dataIdFromObject: KeyFieldsFunction = (object: StoreObject) => {
  // Type-safe way to extract unique identifier
  if (object.uuid && typeof object.uuid === 'string') {
    return object.uuid;
  }
  // adding id as a fallback
  if (object.id && typeof object.id === 'string') {
    return object.id;
  }
  return false; // Fallback if no identifier found
};

const RETRYABLE_STATUS_CODES = [418, 502, 503, 504];

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 5,
    jitter: true,
  },
  attempts: (_count, _operation, error: ServerError) => {
    const maxAttempts = _operation.getContext().retry?.maxAttempts;
    if (maxAttempts !== undefined && _count >= maxAttempts) {
      return false;
    }

    if (RETRYABLE_STATUS_CODES.includes(error.statusCode)) {
      return true;
    }

    return false;
  },
});

// Global error link for logging to Datadog (notifications are handled by individual hooks)
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      // Handle APQ-specific errors
      if (message === 'PersistedQueryNotFound') {
        // This is expected on first request - APQ will auto-retry with full query
        browserLogger.debug(
          `[APQ]: PersistedQueryNotFound for operation ${operation.operationName} - will retry with full query`,
          {
            operationName: operation.operationName,
            apqStatus: 'cache-miss',
          },
        );
        return; // Don't log as error
      }

      if (message === 'PersistedQueryNotSupported') {
        // Server doesn't support APQ - this indicates configuration issue
        browserLogger.warn(
          `[APQ]: Server does not support Automatic Persisted Queries for operation ${operation.operationName}`,
          {
            operationName: operation.operationName,
            apqStatus: 'not-supported',
          },
        );
        return; // Don't log as error
      }

      // Only log errors with severity 'error' or higher, or if no severity is specified
      const severity = extensions?.severity as string | undefined;
      if (!severity || severity === 'error' || severity === 'critical') {
        browserLogger.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Operation: ${operation.operationName}`,
          {
            graphQLError: { message, locations, path, severity: extensions?.severity },
            operationName: operation.operationName,
          },
        );
      }
    });
  }

  if (networkError) {
    // Skip aborted operations (component unmount, navigation, a superseded query).
    // Apollo's httpLink aborts in-flight fetches on unsubscribe; the resulting
    // networkError is a DOMException with name 'AbortError'. These are expected
    // cancellations, not failures, and should not be logged as errors.
    if (isAbortError(networkError)) {
      return;
    }
    browserLogger.error(`[Network error]: ${networkError}, Operation: ${operation.operationName}`, {
      networkError: networkError.message,
      operationName: operation.operationName,
    });
  }
});

export function NewfrontApolloProvider({
  children,
  authProvider,
  config,
  enableAPQ = true,
  additionalTypePolicies,
}: NewfrontApolloProviderProps) {
  const { API } = config;
  const { getToken } = useProvider(authProvider as unknown as Provider<AnyAuthProviderContext>);
  const notifications = useNotifications();

  const [httpLink] = useState(() =>
    createHttpLink({
      uri: API.GRAPHQL_BFF,
    }),
  );

  const [authLink] = useState(() => {
    return setContext(async (_, { headers }) => {
      const token = await getToken();

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
        },
      };
    });
  });

  const [loggerLink] = useState(() =>
    createLoggerLink({
      enabled: true,
      logger: browserLogger,
    }),
  );

  const [notificationLink] = useState(() =>
    createNotificationLink({
      addNotification: notifications.add,
    }),
  );

  const [persistedQueriesLink] = useState(() =>
    createPersistedQueryLink({
      enabled: enableAPQ,
      useGETForHashedQueries: true,
    }),
  );

  const [apolloClient] = useState(
    () =>
      new ApolloClient({
        link: from(
          [
            loggerLink, // 1. Logs operation names before APQ hashing
            authLink, // 2. Auth headers apply to GET requests (APQ uses GET)
            notificationLink, // 3. Notification handling
            errorLink, // 4. Catches APQ-specific errors (PersistedQueryNotFound, etc.)
            retryLink, // 5. Retries APQ failures if needed
            persistedQueriesLink, // 6. APQ transformation (MUST be before httpLink)
            httpLink, // 7. Final network request
          ].filter((link): link is NonNullable<typeof link> => link !== null),
        ),
        cache: new InMemoryCache({
          // This tells Apollo to use the object's ID for cache normalization
          dataIdFromObject,
          typePolicies: {
            Query: {
              fields: {
                getChats: {
                  keyArgs: ['userUuid', 'accountUuid', 'appType', 'statusFilter', 'deletedOnly'],
                },
              },
            },
            ...additionalTypePolicies,
          },
        }),
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'cache-and-network', // Use existing cache data immediately
            nextFetchPolicy: 'cache-and-network', // Then try to update in background
            notifyOnNetworkStatusChange: true, // Important for tracking loading states
          },
          mutate: {
            errorPolicy: 'all', // Return errors in result instead of throwing, allows notification link to process them
          },
        },
      }),
  );

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}

export const graphql = gqlTada;

export type { FragmentOf, ResultOf, VariablesOf } from './graphql';
export { readFragment };

// Export notification context types for consumers
export type { NotificationContextOptions, ExtendedApolloContext, ErrorInfo } from './notifications/types';

// Export APQ types for consumers
export type { PersistedQueryLinkOptions } from './apq';
